/**
 * Challenge URL build/parse + Web Share / clipboard helpers.
 * Query key: `?c=<base64url payload>`
 */
import {
  ChallengePayloadError,
  decodeChallengePayload,
  encodeChallengePayload,
  serializeEditGraph,
  type ChallengeConstraintId,
  type ChallengeEncodeInput,
  type DecodedChallenge,
  type EditGraph,
  type ScoreBreakdown,
} from "@desk/core";
import { getPackEntry, type PackId } from "../../content/registry.js";
import {
  PRESENTATION_QUERY_KEY,
  decodeGamePresentation,
  encodeGamePresentation,
  isDefaultPresentation,
  type GamePresentation,
} from "./presentation.js";

export const CHALLENGE_QUERY_KEY = "c";

export function lookupChallengePack(id: string, version: string) {
  const entry = getPackEntry(id);
  if (!entry || entry.pack.version !== version) return null;
  return entry.pack;
}

export function buildChallengePayloadFromEdit(input: {
  packId: PackId;
  packVersion: string;
  seed: number;
  graph: EditGraph;
  score: Pick<
    ScoreBreakdown,
    "reach" | "contextLoss" | "claimDrift" | "credDelta" | "provenanceRisk"
  >;
  constraintIds?: readonly ChallengeConstraintId[];
}): string {
  const encodeInput: ChallengeEncodeInput = {
    sourcePackId: input.packId,
    sourcePackVersion: input.packVersion,
    seed: input.seed,
    graph: input.graph,
    constraintIds: input.constraintIds ?? [],
    scoreSummary: {
      reach: input.score.reach,
      contextLoss: input.score.contextLoss,
      claimDrift: input.score.claimDrift,
      credDelta: input.score.credDelta,
      provenanceRisk: input.score.provenanceRisk,
    },
  };
  return encodeChallengePayload(encodeInput);
}

export function buildChallengeUrl(
  origin: string,
  pathname: string,
  payload: string,
  presentation?: GamePresentation | null,
): string {
  const base = origin.replace(/\/$/, "");
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const url = new URL(path || "/", base);
  url.searchParams.set(CHALLENGE_QUERY_KEY, payload);
  // Game-side presentation sidecar: only added when it carries real levers, so
  // an unchanged / neutral edit keeps the link byte-for-byte as before.
  if (presentation && !isDefaultPresentation(presentation)) {
    url.searchParams.set(PRESENTATION_QUERY_KEY, encodeGamePresentation(presentation));
  }
  return url.toString();
}

/**
 * Parse the game-side presentation sidecar (`?p=`) from a URL search string.
 * Returns null when absent (old links / neutral edits). A PRESENT-but-malformed
 * sidecar is rejected by `decodeGamePresentation` (throws) — callers that want
 * to degrade gracefully use `tryParsePresentationFromSearch`.
 */
export function parsePresentationFromSearch(search: string): GamePresentation | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = params.get(PRESENTATION_QUERY_KEY);
  if (!raw) return null;
  return decodeGamePresentation(raw);
}

/**
 * Tolerant sidecar parse: null when absent, the decoded sidecar when valid, and
 * null (with the reason) when a present sidecar is malformed. The core `?c=`
 * challenge still loads; only the presentation levers are dropped on a bad
 * sidecar, so a corrupt cosmetic tail never blocks the challenge.
 */
export function tryParsePresentationFromSearch(
  search: string,
): { ok: true; presentation: GamePresentation | null } | { ok: false; reason: string } {
  try {
    return { ok: true, presentation: parsePresentationFromSearch(search) };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : "invalid presentation payload",
    };
  }
}

export function parseChallengeFromSearch(search: string): DecodedChallenge {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = params.get(CHALLENGE_QUERY_KEY);
  if (!raw) {
    throw new ChallengePayloadError("missing", "no challenge payload in URL");
  }
  return decodeChallengePayload(raw, { lookupPack: lookupChallengePack });
}

export function tryParseChallengeFromSearch(
  search: string,
): { ok: true; challenge: DecodedChallenge } | { ok: false; reason: string } {
  try {
    return { ok: true, challenge: parseChallengeFromSearch(search) };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : "invalid challenge link",
    };
  }
}

export type ShareChallengeResult =
  | { method: "web-share"; ok: true }
  | { method: "clipboard"; ok: true }
  | { method: "clipboard"; ok: false; error: string }
  | { method: "none"; ok: false; error: string };

export async function shareChallengeUrl(
  url: string,
  opts: { title?: string; text?: string } = {},
): Promise<ShareChallengeResult> {
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({
        title: opts.title ?? "Beat My Edit: DESK",
        text: opts.text ?? "Same source. Fresh edit. Can you beat my Reach?",
        url,
      });
      return { method: "web-share", ok: true };
    } catch (err) {
      // User cancel → fall through to clipboard; AbortError is fine.
      if (err instanceof DOMException && err.name === "AbortError") {
        // still offer clipboard
      }
    }
  }
  return copyChallengeUrl(url);
}

export async function copyChallengeUrl(url: string): Promise<ShareChallengeResult> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return { method: "clipboard", ok: true };
    }
    // Fallback: legacy execCommand
    if (typeof document !== "undefined") {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) return { method: "clipboard", ok: true };
    }
    return { method: "clipboard", ok: false, error: "Clipboard unavailable" };
  } catch (err) {
    return {
      method: "clipboard",
      ok: false,
      error: err instanceof Error ? err.message : "Copy failed",
    };
  }
}

export function scoreSummaryFromBreakdown(score: ScoreBreakdown) {
  return {
    reach: score.reach,
    contextLoss: score.contextLoss,
    claimDrift: score.claimDrift,
    credDelta: score.credDelta,
    provenanceRisk: score.provenanceRisk,
  };
}

export function graphJsonFromDecoded(challenge: DecodedChallenge): string {
  return serializeEditGraph(challenge.graph);
}
